import { OperatorStatus, type Operator, type OperatorsIdsList } from '@alien-platform/bo-core';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../index';

interface OperatorSliceProps {
  myOperator: Operator | null;
  operatorIdsList: OperatorsIdsList[] | null;
}

const initialState: OperatorSliceProps = {
  myOperator: null,
  operatorIdsList: null,
};

export const operatorSlice = createSlice({
  name: 'operator',
  initialState,
  reducers: {
    setOperatorIdsList(state, action: PayloadAction<OperatorsIdsList[]>) {
      state.operatorIdsList = action.payload;
    },
    setMyOperator(state, action: PayloadAction<Operator>) {
      state.myOperator = action.payload;
    },
  },
});

export const { setMyOperator, setOperatorIdsList } = operatorSlice.actions;

export const getMyOperator = (state: RootState): Operator | null => state.operator.myOperator;

export const getOperatorsAndBrandsMenuList = (
  state: RootState
): { label: string; value: string; parentOpId?: string; isBrand: boolean }[] | null => {
  const { operatorIdsList } = state.operator;

  if (!operatorIdsList) return null;

  return operatorIdsList
    .filter((operator) => operator?.status !== OperatorStatus.DISABLED)
    .map((operator) => ({
      label: `${operator.opId} - ${operator.title}`,
      value: operator.opId,
      parentOpId: operator.parentOpId || undefined,
      isBrand: operator.isBrand,
    }));
};

export const getOperatorsMenuList = (
  state: RootState
): { label: string; value: string; parentOpId?: string }[] | null => {
  const { operatorIdsList } = state.operator;

  if (!operatorIdsList) return null;

  return operatorIdsList
    .filter((operator) => operator?.status !== OperatorStatus.DISABLED)
    .filter((operator) => !operator.isBrand)
    .map((operator) => ({
      label: `${operator.opId} - ${operator.title}`,
      value: operator.opId,
      parentOpId: operator.parentOpId || undefined,
    }));
};

export const getBrandsMenuList = (state: RootState): { label: string; value: string; parentOpId?: string }[] | null => {
  const { operatorIdsList } = state.operator;

  if (!operatorIdsList) return null;

  return operatorIdsList
    .filter((operator) => operator?.status !== OperatorStatus.DISABLED)
    .filter((operator) => operator.isBrand)
    .map((operator) => ({
      label: `${operator.opId} - ${operator.title}`,
      value: operator.opId,
      parentOpId: operator.parentOpId,
    }));
};

// New function to get brands list based on parentOpId
export const getBrandsMenuListOfOperator = (parentOpId: string) => (state: RootState) => {
  const { operatorIdsList } = state.operator;

  if (!operatorIdsList) return null;

  return operatorIdsList
    .filter((operator) => operator?.status !== OperatorStatus.DISABLED)
    .filter((operator) => operator.isBrand && operator.parentOpId === parentOpId)
    .map((operator) => ({
      label: `${operator.opId} - ${operator.title}`,
      value: operator.opId,
      parentOpId: operator.parentOpId,
    }));
};

export const getOperatorsIdList = (state: RootState): OperatorsIdsList[] | null => state.operator.operatorIdsList;
